import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, servicesAPI } from '../utils/api';
import './Dashboard.css';

const DashboardPage = () => {
  const { user } = useAuth();
  const [myJobs, setMyJobs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.user_type === 'organization') {
          const [jobsRes] = await Promise.all([jobsAPI.getMyJobs()]);
          setMyJobs(jobsRes.data.jobs);
        } else {
          const [appsRes, servicesRes] = await Promise.all([jobsAPI.getMyApplications(), servicesAPI.getMyServices()]);
          setMyApplications(appsRes.data.applications);
          setMyServices(servicesRes.data.services);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [user]);

  const APP_STATUS = { pending: { label: 'قيد المراجعة', class: 'badge-warning' }, reviewed: { label: 'تمت المراجعة', class: 'badge-primary' }, shortlisted: { label: 'مختصر', class: 'badge-primary' }, interview: { label: 'مقابلة', class: 'badge-primary' }, accepted: { label: 'مقبول', class: 'badge-success' }, rejected: { label: 'مرفوض', class: 'badge-error' } };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-wrapper">
      <div className="container">
        {/* Welcome */}
        <div className="dash-welcome">
          <div className="dash-avatar">
            {user?.avatar ? <img src={user.avatar} alt={user.full_name} className="avatar avatar-xl" /> :
              <div className="avatar-placeholder avatar-xl" style={{ fontSize: 40 }}>{user?.full_name?.[0]}</div>}
          </div>
          <div>
            <h1>مرحباً، {user?.full_name?.split(' ')[0]}! 👋</h1>
            <p className="text-muted">{user?.user_type === 'organization' ? 'لوحة التحكم الخاصة بمؤسستك' : 'لوحة التحكم الشخصية'}</p>
          </div>
          <div className="dash-actions">
            <Link to="/profile/edit" className="btn btn-ghost">⚙️ تعديل الملف</Link>
            {user?.user_type === 'organization' ? (
              <Link to="/post-job" className="btn btn-primary">+ نشر وظيفة</Link>
            ) : (
              <Link to="/post-service" className="btn btn-primary">+ عرض خدمة</Link>
            )}
          </div>
        </div>

        {user?.user_type === 'organization' ? (
          <>
            <div className="dash-stats-grid">
              {[
                { icon: '💼', value: myJobs.length, label: 'وظيفة منشورة' },
                { icon: '👥', value: myJobs.reduce((a, j) => a + (j.applications_count || 0), 0), label: 'إجمالي المتقدمين' },
                { icon: '✅', value: myJobs.filter(j => j.status === 'active').length, label: 'وظيفة نشطة' },
                { icon: '🔒', value: myJobs.filter(j => j.status === 'closed').length, label: 'وظيفة مغلقة' },
              ].map((s, i) => <div key={i} className="dash-stat card"><span className="dash-stat-icon">{s.icon}</span><span className="dash-stat-value">{s.value}</span><span className="dash-stat-label">{s.label}</span></div>)}
            </div>

            <div className="dash-section">
              <div className="flex-between mb-16"><h2>وظائفي المنشورة</h2><Link to="/post-job" className="btn btn-primary btn-sm">+ وظيفة جديدة</Link></div>
              {myJobs.length === 0 ? (
                <div className="empty-state"><div className="icon">💼</div><h3>لم تنشر أي وظيفة بعد</h3><Link to="/post-job" className="btn btn-primary mt-16">نشر أول وظيفة</Link></div>
              ) : (
                <div className="jobs-table">
                  {myJobs.map(job => (
                    <div key={job.uuid} className="jobs-table-row card">
                      <div>
                        <div className="fw-700">{job.title}</div>
                        <div className="text-muted text-sm">{job.sport_category} • {job.location_city || 'عن بُعد'}</div>
                      </div>
                      <span className={`badge ${job.status === 'active' ? 'badge-success' : 'badge-error'}`}>
                        {job.status === 'active' ? 'نشط' : 'مغلق'}
                      </span>
                      <span className="text-muted text-sm">👥 {job.applications_count} متقدم</span>
                      <div className="flex gap-8">
                        <Link to={`/jobs/${job.uuid}/applications`} className="btn btn-ghost btn-sm">المتقدمون</Link>
                        <Link to={`/jobs/${job.uuid}`} className="btn btn-secondary btn-sm">عرض</Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="dash-stats-grid">
              {[
                { icon: '📝', value: myApplications.length, label: 'طلب مقدم' },
                { icon: '⚡', value: myServices.length, label: 'خدمة معروضة' },
                { icon: '✅', value: myApplications.filter(a => a.status === 'accepted').length, label: 'طلب مقبول' },
                { icon: '⏳', value: myApplications.filter(a => a.status === 'pending').length, label: 'بانتظار الرد' },
              ].map((s, i) => <div key={i} className="dash-stat card"><span className="dash-stat-icon">{s.icon}</span><span className="dash-stat-value">{s.value}</span><span className="dash-stat-label">{s.label}</span></div>)}
            </div>

            <div className="dash-grid-2">
              <div className="dash-section">
                <div className="flex-between mb-16"><h2>طلباتي</h2><Link to="/jobs" className="btn btn-secondary btn-sm">ابحث عن وظائف</Link></div>
                {myApplications.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px 24px' }}><div className="icon">📝</div><h3>لا توجد طلبات</h3><Link to="/jobs" className="btn btn-primary mt-16">استعرض الوظائف</Link></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {myApplications.slice(0, 8).map(app => (
                      <div key={app.id} className="app-row card">
                        <div>
                          <div className="fw-700 text-sm">{app.title}</div>
                          <div className="text-muted text-sm">{app.organization || app.org_name}</div>
                        </div>
                        <span className={`badge ${APP_STATUS[app.status]?.class}`}>{APP_STATUS[app.status]?.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="dash-section">
                <div className="flex-between mb-16"><h2>خدماتي</h2><Link to="/post-service" className="btn btn-primary btn-sm">+ خدمة جديدة</Link></div>
                {myServices.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px 24px' }}><div className="icon">⚡</div><h3>لم تضف خدمة بعد</h3><Link to="/post-service" className="btn btn-primary mt-16">إضافة خدمة</Link></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {myServices.map(s => (
                      <div key={s.uuid} className="app-row card">
                        <div>
                          <div className="fw-700 text-sm">{s.title}</div>
                          <div className="text-muted text-sm">{s.price} {s.price_currency}</div>
                        </div>
                        <span className={`badge ${s.status === 'active' ? 'badge-success' : 'badge-warning'}`}>{s.status === 'active' ? 'نشط' : 'متوقف'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
